/**
 * Hermes Calc — Local Vault (File System Access API).
 *
 * Estratégia:
 *  - O advogado escolhe UMA vez a pasta raiz do Hermes (ex.: ~/Documentos/Hermes).
 *  - Persistimos o `FileSystemDirectoryHandle` no IndexedDB para reutilizar
 *    entre sessões (Chrome/Edge/Opera).
 *  - Toda operação de gravação cria subpastas `[Nome do Cliente]/Originais`
 *    e `[Nome do Cliente]/Cálculos` automaticamente.
 *  - Fallback (Firefox/Safari, ou usuário recusa): usa download tradicional
 *    do navegador para a pasta Downloads padrão.
 *
 * IMPORTANTE: por segurança da web, o `requestPermission` precisa ser
 * disparado por uma AÇÃO DO USUÁRIO (clique). Não roda sozinho na abertura
 * da página. Todas as operações abaixo esperam o handle já existir.
 */

const DB_NAME = "hermes-calc-vault";
const DB_STORE = "handles";
const KEY_ROOT = "root_folder_handle";

// ---------- IndexedDB helpers (mínimos, sem dependência externa) ----------
function idb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(DB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbGet(key) {
  const db = await idb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, "readonly");
    const req = tx.objectStore(DB_STORE).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}
async function idbSet(key, value) {
  const db = await idb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, "readwrite");
    tx.objectStore(DB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
async function idbDel(key) {
  const db = await idb();
  return new Promise((resolve) => {
    const tx = db.transaction(DB_STORE, "readwrite");
    tx.objectStore(DB_STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

// ---------- Capabilities ----------
export function fsApiSupported() {
  return typeof window !== "undefined"
    && typeof window.showDirectoryPicker === "function";
}

// ---------- Root handle ----------
export async function getRootHandle() {
  if (!fsApiSupported()) return null;
  try {
    const h = await idbGet(KEY_ROOT);
    return h || null;
  } catch { return null; }
}
export async function clearRootHandle() {
  await idbDel(KEY_ROOT);
}

export async function ensurePermission(handle, mode = "readwrite") {
  if (!handle || !handle.queryPermission) return false;
  const q = await handle.queryPermission({ mode });
  if (q === "granted") return true;
  const r = await handle.requestPermission({ mode });
  return r === "granted";
}

/** Requer clique do usuário — abre o seletor nativo e persiste o handle. */
export async function pickRootHandle() {
  if (!fsApiSupported()) throw new Error("File System Access API não suportada neste navegador");
  const handle = await window.showDirectoryPicker({
    id: "hermes-calc-root",
    mode: "readwrite",
    startIn: "documents",
  });
  const ok = await ensurePermission(handle, "readwrite");
  if (!ok) throw new Error("Permissão de escrita negada");
  await idbSet(KEY_ROOT, handle);
  // cria "Hermes" root marker
  await ensureSubdir(handle, "_hermes_ok");
  return handle;
}

async function ensureSubdir(parent, name) {
  return parent.getDirectoryHandle(name, { create: true });
}

const safe = (s) => (s || "sem-nome")
  .toString()
  .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^\w\-\.\(\) ]/g, "_")
  .replace(/\s+/g, " ")
  .trim()
  .slice(0, 120) || "sem-nome";

/**
 * Escreve um arquivo em `<root>/<client>/<subfolder>/<filename>`.
 * subfolder típico: "Originais" ou "Cálculos".
 * Se algo falhar, retorna { ok:false, reason }. Nunca lança.
 */
export async function writeToClientFolder({ client, subfolder, filename, blob }) {
  const root = await getRootHandle();
  if (!root) return { ok: false, reason: "no_root_handle" };
  const ok = await ensurePermission(root, "readwrite");
  if (!ok) return { ok: false, reason: "no_permission" };

  try {
    const clientDir = await ensureSubdir(root, safe(client));
    const subDir = await ensureSubdir(clientDir, safe(subfolder || "Documentos"));
    const fh = await subDir.getFileHandle(safe(filename), { create: true });
    const w = await fh.createWritable();
    await w.write(blob);
    await w.close();
    return { ok: true, path: `${safe(client)}/${safe(subfolder)}/${safe(filename)}` };
  } catch (e) {
    return { ok: false, reason: e?.message || "write_error" };
  }
}

/**
 * Baixa um arquivo do backend E, se houver root handle, também grava na
 * pasta local do cliente. Se não houver, chama download tradicional.
 */
export async function downloadToClientVault({ url, headers, client, subfolder, filename }) {
  const res = await fetch(url, { headers, credentials: "include" });
  if (!res.ok) throw new Error(`Falha ao baixar do servidor (HTTP ${res.status})`);
  const blob = await res.blob();

  // Tenta pasta local do cliente
  const write = await writeToClientFolder({ client, subfolder, filename, blob });
  if (write.ok) return { ok: true, mode: "vault", path: write.path };

  // Fallback: download tradicional
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(a.href);
    a.remove();
  }, 1000);
  return { ok: true, mode: "download", reason: write.reason };
}
